import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase/firebase-config';
import SheetModal from '../../JobView/SheetModal';
import SheetDetailModal from '../../JobView/Tabs/SheetDetailModal'; // NUEVO
import { collection, query, onSnapshot, doc, updateDoc, getDoc, getDocs, addDoc, deleteDoc, where, writeBatch, arrayUnion } from 'firebase/firestore';
import { Plus, User, Sparkles, LayoutGrid, Bot } from 'lucide-react';
import { buildTaskAssignment, buildMultiAssignment, buildAssignmentEmail, buildAssignedByStamp } from '../../../utils/taskAssignment';
import MultiUserSelect from './Modals/MultiUserSelect';
import TaskKanbanBoard from './TaskKanbanBoard';
import TaskDetailModal, { TYPE_META } from './Modals/TaskDetailModal';
import TaskAgentModal from '../TaskAgentModal';
import { productionResetFields } from '../../../lib/production';

const TasksAndChecklistTab = ({ user, jobId, storage }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSheetData, setSelectedSheetData] = useState(null);
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskAssignees, setNewTaskAssignees] = useState([]); // ids de usuarios para la tarea nueva
  const [selectedTaskId, setSelectedTaskId] = useState(null); // ficha abierta en la ventana de detalle
  const [isAgentOpen, setIsAgentOpen] = useState(false); // asistente/agente de tareas
  const [teamMembers, setTeamMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // todos los usuarios para asignación
  const [jobData, setJobData] = useState(null);
  const [sheetForDetails, setSheetForDetails] = useState(null); // NUEVO

  useEffect(() => {
    if (!jobId || !db) return;

    const jobDocRef = doc(db, 'jobs', jobId);
    const unsubscribeJob = onSnapshot(jobDocRef, async (docSnap) => {
        if (docSnap.exists()) {
            const currentJobData = { id: docSnap.id, ...docSnap.data() };
            setJobData(currentJobData);

            if (currentJobData.assignedRoles && currentJobData.assignedRoles.length > 0) {
                try {
                    const usersRef = collection(db, "users");
                    const q = query(usersRef, where("roles", "array-contains-any", currentJobData.assignedRoles));
                    const usersSnapshot = await getDocs(q);
                    setTeamMembers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                } catch (error) {
                    console.error("Error fetching job team members:", error);
                }
            } else {
                setTeamMembers([]);
            }
        }
    });

    const tasksCollectionRef = collection(db, 'jobs', jobId, 'tasks');
    const qTasks = query(tasksCollectionRef);
    const unsubscribeTasks = onSnapshot(qTasks, (snapshot) => {
      setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching tasks:", error);
      setLoading(false);
    });

    return () => {
        unsubscribeJob();
        unsubscribeTasks();
    };
  }, [jobId, db]);

  // Lista COMPLETA de usuarios del sistema para la asignación (cualquier usuario).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        if (!cancelled) setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('Error cargando usuarios para asignación:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Asigna una tarea a un usuario real y le envía UN correo (mismo criterio que Prepress).
  const handleAssignUser = async (task, userId) => {
    const taskRef = doc(db, 'jobs', jobId, 'tasks', task.id);
    const assignedUser = allUsers.find(u => u.id === userId) || null;
    try {
      const batch = writeBatch(db);
      if (assignedUser) {
        batch.update(taskRef, { ...buildTaskAssignment(assignedUser), ...buildAssignedByStamp(user) });
        const mail = buildAssignmentEmail({
          assignedUser,
          assignedBy: user,
          taskTitle: task.title,
          taskDescription: task.description || '',
          jobId,
          suggestedAction: task.aiSuggestedAction,
        });
        if (mail) batch.set(doc(collection(db, 'mail')), mail);
      } else {
        // "Sin asignar": limpia la asignación, no envía correo.
        batch.update(taskRef, { assignedTo: null, assignedToUid: null, assignedToEmail: null, assignedToName: null });
      }
      await batch.commit();
    } catch (error) {
      console.error('Error asignando tarea:', error);
    }
  };

  // Reasigna una tarea de checklist a varios usuarios; envía correo solo a los nuevos.
  const handleAssignUsers = async (task, ids) => {
    const prev = task.assignedToUids || (task.assignedTo ? [task.assignedTo] : []);
    const assignedUsers = allUsers.filter(u => ids.includes(u.id));
    const newlyAdded = assignedUsers.filter(u => !prev.includes(u.id));
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'jobs', jobId, 'tasks', task.id), { ...buildMultiAssignment(assignedUsers), ...buildAssignedByStamp(user) });
      newlyAdded.forEach(u => {
        const mail = buildAssignmentEmail({ assignedUser: u, assignedBy: user, taskTitle: task.title, taskDescription: task.description || '', jobId, suggestedAction: task.aiSuggestedAction });
        if (mail) batch.set(doc(collection(db, 'mail')), mail);
      });
      await batch.commit();
    } catch (error) {
      console.error('Error asignando tarea (multi):', error);
    }
  };

  const handleCorrectSheet = async (task) => {
    const sheetId = task.sheetId || task.relatedPlateId;
    if (!db || !jobId || !sheetId) {
        alert("Error: Faltan datos para abrir la plancha.");
        return;
    }
    try {
        const sheetRef = doc(db, `jobs/${jobId}/sheets`, sheetId);
        const sheetSnap = await getDoc(sheetRef);
        if (sheetSnap.exists()) {
            setSelectedSheetData({ ...sheetSnap.data(), id: sheetSnap.id, taskId: task.id });
            setIsModalOpen(true);
        } else {
            alert("Error: No se encontró la plancha asociada.");
        }
    } catch (error) {
        console.error("Error fetching sheet for correction:", error);
        alert("Ocurrió un error al abrir la plancha.");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSheetData(null);
  };

  // NUEVO: Abre SheetDetailModal para tareas de reproducción
  const handleViewReproductionSheet = async (task) => {
    const sheetId = task.sheetId;
    if (!db || !jobId || !sheetId) {
      alert("Error: Esta tarea no tiene una plancha asociada.");
      return;
    }
    try {
      const sheetRef = doc(db, `jobs/${jobId}/sheets`, sheetId);
      const sheetSnap = await getDoc(sheetRef);
      if (sheetSnap.exists()) {
        setSheetForDetails({ ...sheetSnap.data(), id: sheetSnap.id });
      } else {
        alert("No se encontró la plancha asociada.");
      }
    } catch (error) {
      console.error("Error abriendo plancha de reproducción:", error);
      alert("Ocurrió un error al abrir la plancha.");
    }
  };

  const handleTaskCompletion = async (taskId) => {
      if(!taskId) return;
      const task = tasks.find(t => t.id === taskId);
      const taskRef = doc(db, 'jobs', jobId, 'tasks', taskId);
      try {
        const batch = writeBatch(db);
        batch.update(taskRef, {
            status: 'completed',
            completedAt: new Date(),
            completedBy: user.email,
        });

        // Al cerrar una ALERTA (corrección o reproducción), la plancha vuelve a
        // "Pendiente" y se reinicia el flujo de producción (decisión de negocio).
        // El reinicio de contadores ya ocurrió al REPORTAR la falla; se repite aquí a
        // propósito: es idempotente y cubre las alertas creadas antes de ese cambio.
        const isAlert = task && (task.type === 'system' || task.type === 'system_reproduction');
        const sheetId = task && (task.sheetId || task.relatedPlateId);
        if (isAlert && sheetId) {
            const sheetRef = doc(db, 'jobs', jobId, 'sheets', sheetId);
            batch.update(sheetRef, {
                ...productionResetFields(),
                status: 'Listo para Prepress',
                hasActiveAlert: false,
                history: arrayUnion({
                    action: 'Plancha reiniciada a Pendiente (alerta resuelta)',
                    user: user.email,
                    timestamp: new Date(),
                }),
            });
        }

        await batch.commit();
      } catch(error) {
          console.error("Error completing task: ", error);
      }
  };

  const handleAddManualTask = async () => {
    if (!newTaskDescription.trim() || !user || !jobId) {
        alert("No se puede agregar una tarea vacía.");
        return;
    }
    // Asignación OBLIGATORIA a uno o varios usuarios.
    if (newTaskAssignees.length === 0) {
        alert("Debes asignar la tarea a al menos un usuario.");
        return;
    }
    const assignedUsers = allUsers.filter(u => newTaskAssignees.includes(u.id));
    try {
      const batch = writeBatch(db);
      const taskRef = doc(collection(db, 'jobs', jobId, 'tasks'));
      batch.set(taskRef, {
        title: newTaskDescription,
        status: 'pending',
        type: 'manual',
        createdBy: user.email,
        createdByName: user.displayName || user.email,
        createdAt: new Date(),
        ...buildMultiAssignment(assignedUsers),
      });
      // UN correo a cada usuario asignado.
      assignedUsers.forEach(u => {
        const mail = buildAssignmentEmail({ assignedUser: u, assignedBy: user, taskTitle: newTaskDescription, taskDescription: '', jobId });
        if (mail) batch.set(doc(collection(db, 'mail')), mail);
      });
      await batch.commit();
      setNewTaskDescription('');
      setNewTaskAssignees([]);
    } catch (error) {
      console.error("Error adding manual task:", error);
    }
  };

  // Renombra una tarea (desde la ventana de detalle del checklist).
  const handleRenameTask = async (task, newTitleRaw) => {
    const newTitle = (newTitleRaw || '').trim();
    if (!newTitle || newTitle === task.title) return;
    try {
      await updateDoc(doc(db, 'jobs', jobId, 'tasks', task.id), { title: newTitle });
    } catch (error) {
      console.error('Error editando tarea:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar esta tarea?")) {
        const taskRef = doc(db, 'jobs', jobId, 'tasks', taskId);
        try {
            await deleteDoc(taskRef);
        } catch (error) {
            console.error("Error deleting task:", error);
        }
    }
  };
  
  // Mueve una tarea entre carriles del Kanban (cambia su estado).
  // A "Hecho" reutiliza handleTaskCompletion (que además reinicia la plancha si es alerta).
  const handleMoveTask = async (taskId, newStatus) => {
    // El tablero ahora avisa TAMBIEN cuando se suelta dentro de la misma columna
    // (S3 lo necesita para reordenar). Aqui el orden no se persiste, asi que un
    // drop que no cambia el estado no debe escribir nada.
    const actual = tasks.find((t) => t.id === taskId);
    if (actual && actual.status === newStatus) return;
    if (newStatus === 'completed') { await handleTaskCompletion(taskId); return; }
    try {
      await updateDoc(doc(db, 'jobs', jobId, 'tasks', taskId), {
        status: newStatus,        // 'pending' | 'in_progress'
        completedAt: null,
        completedBy: null,
      });
    } catch (error) {
      console.error('Error moviendo tarea:', error);
    }
  };

  const formatDate = (timestamp) => {
      if (!timestamp) return 'Fecha no disponible';
      return timestamp.toDate ? timestamp.toDate().toLocaleString() : new Date(timestamp).toLocaleString();
  }

  if (loading) {
    return <div className="p-4 text-center">Cargando tareas...</div>;
  }

  const selectedTask = tasks.find(t => t.id === selectedTaskId) || null;

  // Ficha COMPACTA del Kanban. Clic = abre la ventana de detalle; arrastrar = cambia estado.
  const renderTaskCard = (task) => {
    const meta = TYPE_META[task.type] || TYPE_META.manual;
    const isCompleted = task.status === 'completed';
    return (
      <div
        onClick={() => setSelectedTaskId(task.id)}
        className={`p-2.5 rounded-md shadow-sm bg-white hover:shadow-md ${meta.card}`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.badge}`}>{meta.label}</span>
          {task.aiSuggestedAction && <Sparkles size={12} className="text-purple-500 flex-shrink-0" />}
        </div>
        <p className={`text-sm font-medium leading-snug ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{task.title}</p>
        <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1 truncate"><User size={11} /> {task.assignedToName || 'Sin asignar'}</p>
      </div>
    );
  };

  // Barra superior para añadir tareas al checklist (asignación obligatoria).
  const addBar = (
    <div className="mb-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={newTaskDescription}
          onChange={(e) => setNewTaskDescription(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddManualTask()}
          placeholder="Añadir nueva tarea al checklist..."
          className="flex-grow min-w-[200px] p-2 border rounded-md"
        />
        <MultiUserSelect users={allUsers} selectedIds={newTaskAssignees} onChange={setNewTaskAssignees} placeholder="Asignar a (obligatorio)..." />
        <button
          onClick={handleAddManualTask}
          disabled={!newTaskDescription.trim() || newTaskAssignees.length === 0}
          className="p-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-1 disabled:bg-gray-400"
        >
          <Plus size={16} /> Añadir
        </button>
      </div>
      <p className="text-xs text-gray-500">Las tareas de checklist requieren al menos un usuario asignado. Haz clic en una ficha para ver el detalle; arrástrala entre carriles para cambiar su estado.</p>
    </div>
  );

  return (
    <div className="p-4 h-full flex flex-col min-h-0">
      {isModalOpen && selectedSheetData && jobData && (
        <SheetModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          jobData={jobData}
          db={db}
          storage={storage}
          user={user}
          initialSheet={selectedSheetData}
          taskId={selectedSheetData.taskId}
          onSaveSuccess={() => handleTaskCompletion(selectedSheetData.taskId)}
        />
      )}

      {sheetForDetails && jobData && (
        <SheetDetailModal
          isOpen={true}
          onClose={() => setSheetForDetails(null)}
          sheet={sheetForDetails}
          db={db}
          storage={storage}
          jobData={jobData}
          user={user}
          allowProductionStatus={true}
        />
      )}

      <div className="flex-grow min-h-0">
        <TaskKanbanBoard
          title="Tablero de Tareas"
          icon={<LayoutGrid className="text-indigo-500" />}
          tasks={tasks}
          onMove={handleMoveTask}
          renderCard={renderTaskCard}
          headerExtra={addBar}
          headerAction={(
            <button
              onClick={() => setIsAgentOpen(true)}
              className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white bg-emerald-600 rounded-md hover:bg-emerald-700"
            >
              <Bot size={16} /> Asistente de Tareas
            </button>
          )}
          emptyText="Sin tareas."
        />
      </div>

      <TaskDetailModal
        task={selectedTask}
        onClose={() => setSelectedTaskId(null)}
        allUsers={allUsers}
        onAssignSingle={handleAssignUser}
        onAssignMulti={handleAssignUsers}
        onCorrect={handleCorrectSheet}
        onViewSheet={handleViewReproductionSheet}
        onMove={handleMoveTask}
        onDelete={handleDeleteTask}
        onSaveTitle={handleRenameTask}
        formatDate={formatDate}
      />

      <TaskAgentModal
        isOpen={isAgentOpen}
        onClose={() => setIsAgentOpen(false)}
        jobId={jobId}
        jobName={jobData?.jobName}
        user={user}
      />
    </div>
  );
};

export default TasksAndChecklistTab;